import React from 'react';
import { USERS } from './data.jsx';
import { Avatar, Icon, getUser } from './components.jsx';
// L&T IT Helpdesk - Rota
//
// Weekly shift / availability board. One column per IT team member, one
// row per day. Anyone in the IT group can edit any cell - it's a shared
// planning surface, not personal. Edits propagate to other tabs via the
// page-wide 20s background poll equivalent here (15s rota-only refresh).
//
// Cells store free-text hours strings ("9-5", "WFH", "Off", "10-4 in
// office") so we don't have to pre-commit to a fixed schedule model. The
// data shape is one row per (userId, date) in SQL.

const { useState, useEffect, useCallback, useRef } = React;

const rotaApi = (method, path, body) => window.IT_AUTH.api(method, path, body, 'rotaApi');

// ─── Date helpers ─────────────────────────────────────────────────────────
// Use UTC for storage / comparisons, but display in local time. Helpdesk
// sits in one timezone, but DST-safe rollover is still worth getting right.
const toDateKey = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
const startOfWeek = (d) => {
  // Monday-start week. Convert Sun (0) → 6 days back, Mon (1) → 0, …
  const day = (d.getDay() + 6) % 7;
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  out.setDate(out.getDate() - day);
  return out;
};
const addDays = (d, n) => {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
};
const fmtWeekHeader = (mondayStart) => {
  // Working week = Mon-Fri. End of the displayed range is Friday.
  const friday = addDays(mondayStart, 4);
  const sameMonth = mondayStart.getMonth() === friday.getMonth();
  const opts = { day: 'numeric', month: 'short' };
  const left = mondayStart.toLocaleDateString('en-GB', sameMonth ? { day: 'numeric' } : opts);
  const right = friday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${left} – ${right}`;
};

// Default-hours quick-fill chips. Click to apply common patterns instead of
// typing - most of the time it's one of these.
const QUICK_OPTIONS = ['8:30-4:30', '8:30-6', '9-5', '9:30-5:30', '10-6', '10:30-6', 'AL', 'Half day', 'NWD'];

// Remote is layered on top of a shift rather than being its own option, so
// the hours value can be "9-5 (Remote)" - IT person works 9-5 from home.
// Older entries used "(WFH)" - both tokens are still recognised so the
// existing data renders correctly, but new saves write "(Remote)".
//   hasRemoteMarker(v) - does the value already include the Remote/WFH marker?
//   setRemote(value)   - ensure the (Remote) suffix is present (idempotent).
//   stripRemote(value) - ensure the Remote/WFH marker is absent (idempotent).
// Office and Remote render in the editor as a radio-style pair: setRemote
// is the "Remote" button, stripRemote is the "Office" button.
const REMOTE_RE = /\s*\(?\s*(?:wfh|remote)\s*\)?/i;
const hasRemoteMarker = (v) => /\b(?:wfh|remote)\b/i.test(v || '');
// Ensure the value carries the (Remote) marker - idempotent. Pairs with
// stripRemote so the Office and Remote buttons behave like radios.
const setRemote = (v) => {
  const s = (v || '').trim();
  if (hasRemoteMarker(s)) return s;
  // Treat the standalone "Office" placeholder as empty - it's only there
  // to flip the cell into Office mode pre-hours; once we switch to Remote,
  // the right text is just "Remote", not "Office (Remote)".
  if (s.toLowerCase() === 'office') return 'Remote';
  return s ? `${s} (Remote)` : 'Remote';
};
// Strip the Remote / WFH marker so the cell renders as Office. Idempotent.
const stripRemote = (v) => (v || '').replace(REMOTE_RE, '').trim();

// ─── Coverage check ───────────────────────────────────────────────────────
// The IT helpdesk should be covered 08:30 – 18:00 Mon-Fri. We parse the
// hours strings into [start,end] minute ranges, merge across the team for
// each day, and report whether 08:30-18:00 is fully spanned.
//
// Shifts are AM-PM by convention so we don't need 24h input: if the end
// hour reads lower than the start, we add 12 ("8-6" → 08:00-18:00,
// "8:30-4:30" → 08:30-16:30). AL / Half day / WFH-only / freeform text
// don't contribute coverage - only the parseable shift ranges do.
const COVER_START = 8 * 60 + 30;  // 08:30
const COVER_END = 18 * 60;        // 18:00

const parseShift = (val) => {
  if (!val) return null;
  // Drop the (WFH) marker first - WFH counts toward coverage, the person
  // is still working, just remote. AL / Half day don't.
  const cleaned = (val || '').replace(REMOTE_RE, '').trim();
  const lower = cleaned.toLowerCase();
  if (!cleaned) return null;
  if (lower === 'al' || lower === 'off' ||
      lower.includes('holiday') || lower.includes('annual') ||
      lower.includes('half')) return null;
  const m = /^(\d{1,2})(?::(\d{2}))?\s*-\s*(\d{1,2})(?::(\d{2}))?/.exec(cleaned);
  if (!m) return null;
  const sH = parseInt(m[1], 10);
  const sM = m[2] ? parseInt(m[2], 10) : 0;
  let eH = parseInt(m[3], 10);
  const eM = m[4] ? parseInt(m[4], 10) : 0;
  // End < start → PM convention ("8-6" means 8am-6pm, not 8am-6am).
  if (eH < sH) eH += 12;
  const start = sH * 60 + sM;
  const end = eH * 60 + eM;
  if (end <= start) return null;
  return { start, end };
};

// Returns the un-covered sub-intervals of [COVER_START, COVER_END], so we
// can tell the user "Mon 8:30-9 not covered" instead of a yes/no flag.
const coverageGaps = (shifts) => {
  const sorted = shifts.slice().sort((a, b) => a.start - b.start);
  const gaps = [];
  let cursor = COVER_START;
  for (const s of sorted) {
    if (s.start > cursor) {
      gaps.push({ start: cursor, end: Math.min(s.start, COVER_END) });
    }
    if (s.end > cursor) cursor = s.end;
    if (cursor >= COVER_END) return gaps;
  }
  if (cursor < COVER_END) gaps.push({ start: cursor, end: COVER_END });
  return gaps;
};

// Format minutes-since-midnight as the team's shorthand: "8:30", "9", "6".
// We collapse trailing :00 and drop the PM 12 so the output matches what
// people would type into a cell ("6" rather than "18:00").
const fmtTime = (mins) => {
  let h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 12) h -= 12;
  return m === 0 ? `${h}` : `${h}:${String(m).padStart(2, '0')}`;
};
const fmtRange = (gap) => `${fmtTime(gap.start)}–${fmtTime(gap.end)}`;

// ─── Cell ─────────────────────────────────────────────────────────────────
// Click to edit, blur or Enter to save, Esc to cancel. Optimistic - the
// new value shows immediately, the network call is fire-and-forget with
// a rollback on failure.
const RotaCell = ({ value, onSave, isToday, isWeekend, isHoliday, holidayName }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  // Hover is React state, not an imperative style mutation. Earlier the
  // mouseenter/leave handlers wrote directly to `el.style.background`,
  // which silently fell out of sync whenever the cell's computed bg
  // changed underneath (e.g. picking a chip turned an empty cell into
  // an Office cell, but the imperative hover-grey stayed pinned in the
  // DOM until the mouse actually moved out). Letting React own the
  // colour fixes the after-save flicker.
  const [hovering, setHovering] = useState(false);
  const inputRef = useRef();

  useEffect(() => { setDraft(value || ''); }, [value]);
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = () => {
    const next = draft.trim();
    if (next !== (value || '').trim()) onSave(next);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(value || '');
    setEditing(false);
  };

  // Bank holiday - greyed, non-editable, and overrides whatever's stored (the
  // stored value is kept in the DB, just not shown). The hooks above still run
  // every render, so this early return is rules-of-hooks safe.
  if (isHoliday) {
    return (
      <div style={{
        minHeight: 56, padding: '8px 10px',
        background: '#F1F0EE',
        border: '1px solid var(--line)', borderTop: 'none', borderLeft: 'none',
        display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2,
        cursor: 'default',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#A8A29E' }}>Bank Holiday</span>
        {holidayName && (
          <span style={{ fontSize: 10, color: '#C4C0BB', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {holidayName}
          </span>
        )}
      </div>
    );
  }

  const displayVal = value && value.trim();
  const valueLower = (displayVal || '').toLowerCase();
  const isRemote = hasRemoteMarker(displayVal);
  // "AL" (annual leave) is the new label, "Off" / "holiday" / "annual"
  // covered as well so any earlier saves still render with the muted-grey
  // styling instead of looking like a regular shift.
  const isOff = valueLower === 'al' || valueLower === 'off' || valueLower.includes('holiday') || valueLower.includes('annual');
  const isHalfDay = valueLower.includes('half');
  // NWD = explicit "Non Working Day" - distinct from AL because it isn't
  // leave, it's just a day someone doesn't work (part-timer, contracted
  // four-day week, etc.). Visually close to AL but warmer-toned so the
  // two don't blur together at a glance.
  const isNwd = valueLower === 'nwd' || valueLower.includes('non working') || valueLower.includes('non-working');
  // Classify a non-leave entry. A "working" day is a real timed shift, the
  // bare "Office" placeholder, or a Remote-marked entry - these get the
  // Office / Remote indicator. Anything else that's just free text
  // ("Conference", "Training", offsite…) is the "Other" category: the person
  // isn't on leave, but they're not at a normal desk either, so it must not
  // be badged Office. parseShift() returns a range only for real hours.
  const parsedShift = parseShift(displayVal);
  const isOfficeWord = valueLower === 'office';
  const isWorking = !!displayVal && !isOff && !isHalfDay && !isNwd && (!!parsedShift || isOfficeWord || isRemote);
  const isOther = !!displayVal && !isOff && !isHalfDay && !isNwd && !isWorking;
  // Office is the default working state - Remote is the explicit opt-out.
  const isOffice = isWorking && !isRemote;
  // Text colour for the value. Off in muted grey; Remote text in deep blue
  // (matches the cell-level blue tint applied below). In-office in default text.
  let valueColor = 'var(--text)';
  if (isOff) valueColor = '#B0BEC7';
  else if (isNwd) valueColor = '#A8A29E';
  else if (isOther) valueColor = '#6D28D9';
  else if (isRemote) valueColor = '#1E40AF';
  else if (isOffice) valueColor = '#065F46';

  // Cell background - Remote gets a pale blue wash, Office gets a pale green
  // wash, so the team can scan who's where at a glance without reading the
  // hours text. Weekend / today still win when nothing's typed.
  let cellBg = isWeekend ? '#FAFAF9' : isToday ? '#FEFCE8' : '#fff';
  if (isRemote) cellBg = '#EFF6FF';
  else if (isOffice) cellBg = '#ECFDF5';
  // AL takes a very light grey wash so the row reads "nobody" at a glance,
  // overriding today's yellow tint - otherwise the current-day column
  // breaks the visual consistency of an all-AL row. Kept pale so it
  // recedes rather than dominates.
  else if (isOff) cellBg = '#F8FAFC';
  // NWD uses the warm-stone palette instead of cool-slate, so a row with
  // mixed AL and NWD reads as two clearly different states rather than
  // "more or less the same grey".
  else if (isNwd) cellBg = '#F5F5F4';
  // Other (conference / training / offsite) - a pale violet wash, distinct
  // from office-green and remote-blue so "not at a desk" reads at a glance.
  else if (isOther) cellBg = '#F5F3FF';

  return (
    <div
      onClick={() => !editing && setEditing(true)}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        minHeight: 56,
        padding: '8px 10px',
        // Hover only applies when NOT editing - once you're typing the
        // bg should track the actual value, not a hover tint.
        background: !editing && hovering ? '#F8FAFC' : cellBg,
        border: '1px solid var(--line)',
        borderTop: 'none', borderLeft: 'none',
        cursor: editing ? 'text' : 'pointer',
        position: 'relative',
        transition: 'background 120ms ease',
      }}
    >
      {editing ? (
        <>
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              else if (e.key === 'Escape') cancel();
            }}
            placeholder="9-5, AL, Conference…"
            style={{
              width: '100%', border: 'none', outline: 'none',
              background: 'transparent', fontSize: 13, fontFamily: 'inherit',
              padding: 0,
            }}
          />
          <div style={{ marginTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {QUICK_OPTIONS.map((opt) => (
              <button
                key={opt}
                onMouseDown={(e) => {
                  // mousedown (not click) so the input's blur doesn't fire
                  // first and dismiss us with the wrong draft value.
                  e.preventDefault();
                  // Preserve a pre-existing WFH marker so picking a shift
                  // after toggling WFH (or vice versa) doesn't wipe it.
                  const next = hasRemoteMarker(draft) ? `${opt} (Remote)` : opt;
                  setDraft(next);
                  onSave(next);
                  // Stay open - user may want to layer WFH on top, or
                  // re-pick a different shift. They close via blur (click
                  // outside) or Enter.
                }}
                style={{
                  fontSize: 10, fontWeight: 600, padding: '2px 6px',
                  background: 'var(--bg-sub)', border: '1px solid var(--line)',
                  borderRadius: 4, cursor: 'pointer', color: 'var(--text-muted)',
                }}
              >
                {opt}
              </button>
            ))}
            {/* Office / Remote - a radio-style pair. They live alongside the
                shift chips and let you declare *where* you'll be either
                before or after picking the hours. Whichever matches the
                current draft is shown filled; the other is hollow. Click
                Office on a Remote draft to flip it (and vice versa). Empty
                draft + Office click drops an 'Office' placeholder so the
                tint kicks in. */}
            {(() => {
              const remote = hasRemoteMarker(draft);
              const t = draft.trim();
              const tl = t.toLowerCase();
              const draftParsed = parseShift(t);
              const draftLeave = tl === 'al' || tl === 'off' || tl === 'nwd'
                || tl.includes('holiday') || tl.includes('annual') || tl.includes('half')
                || tl.includes('non working') || tl.includes('non-working');
              // Office active only for a real shift or the bare "Office" word -
              // free text like "Conference" is Other, not Office.
              const officeActive = !remote && (!!draftParsed || tl === 'office');
              const otherActive = !!t && !remote && !draftParsed && tl !== 'office' && !draftLeave;
              return (
                <>
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      let next;
                      if (remote) {
                        // Remote set → strip the marker, keep the hours.
                        next = stripRemote(draft);
                      } else if (!draft.trim()) {
                        // Empty cell → drop an 'Office' placeholder so the
                        // green tint kicks in. When the user picks a shift
                        // chip next, the chip handler replaces 'Office'
                        // with the hours (still no marker → still Office).
                        next = 'Office';
                      } else {
                        // Already an office shift / AL / NWD / Half day -
                        // leave the value alone.
                        return;
                      }
                      setDraft(next);
                      onSave(next);
                    }}
                    style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px',
                      background: officeActive ? '#047857' : '#ECFDF5',
                      border: `1px solid ${officeActive ? '#047857' : '#A7F3D0'}`,
                      borderRadius: 4, cursor: 'pointer',
                      color: officeActive ? '#fff' : '#047857',
                      letterSpacing: 0.3,
                    }}
                    title={remote ? 'Switch to in office' : 'Mark as in office'}
                  >
                    {officeActive ? '✓ Office' : '+ Office'}
                  </button>
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      const next = setRemote(draft);
                      if (next === draft.trim()) return;
                      setDraft(next);
                      onSave(next);
                    }}
                    style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px',
                      background: remote ? '#1E40AF' : '#EFF6FF',
                      border: `1px solid ${remote ? '#1E40AF' : '#BFDBFE'}`,
                      borderRadius: 4, cursor: 'pointer',
                      color: remote ? '#fff' : '#1E40AF',
                      letterSpacing: 0.3,
                    }}
                    title={remote ? 'Already marked as Remote' : 'Mark as Remote'}
                  >
                    {remote ? '✓ Remote' : '+ Remote'}
                  </button>
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      // Empty cell → drop an 'Other' placeholder they can
                      // rename (e.g. to 'Conference'). If there's already
                      // content, leave it - switching an office shift to
                      // Other would otherwise wipe the hours. Use Clear first.
                      if (!t) { setDraft('Other'); onSave('Other'); }
                    }}
                    style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 8px',
                      background: otherActive ? '#6D28D9' : '#F5F3FF',
                      border: `1px solid ${otherActive ? '#6D28D9' : '#DDD6FE'}`,
                      borderRadius: 4, cursor: 'pointer',
                      color: otherActive ? '#fff' : '#6D28D9',
                      letterSpacing: 0.3,
                    }}
                    title="Mark as Other - conference, training, offsite…"
                  >
                    {otherActive ? '✓ Other' : '+ Other'}
                  </button>
                </>
              );
            })()}
            {/* Clear - wipes the cell value entirely. Only offered when there
                is something to wipe; saving an empty string triggers a DELETE
                on the server (see saveCell). Tucked at the end of the chip
                row in muted red so it doesn't compete with the regular
                presets. */}
            {(draft && draft.trim()) && (
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  setDraft('');
                  onSave('');
                  setEditing(false);
                }}
                style={{
                  fontSize: 10, fontWeight: 600, padding: '2px 8px',
                  background: 'transparent', border: '1px solid #FECACA',
                  borderRadius: 4, cursor: 'pointer', color: '#B91C1C',
                  marginLeft: 'auto',
                }}
                title="Clear this cell"
              >
                × Clear
              </button>
            )}
          </div>
        </>
      ) : (
        displayVal ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Hide the trailing "(WFH)" in the hours line - it's already
                indicated by the badge below, and the cell tint. AL and
                NWD get spelled out for readability ('AL' → 'Annual Leave',
                'NWD' → 'Non Working Day'). */}
            <span style={{ fontSize: 13, fontWeight: 500, color: valueColor }}>
              {isOff
                ? 'Annual Leave'
                : isNwd
                  ? 'Non Working Day'
                  : isRemote
                    ? displayVal.replace(REMOTE_RE, '').trim() || 'Remote'
                    : displayVal}
            </span>
            {isWorking && (
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: isRemote ? '#1E40AF' : '#047857',
              }}>
                {isRemote ? 'Remote' : 'Office'}
              </span>
            )}
            {isOther && (
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                textTransform: 'uppercase', color: '#6D28D9',
              }}>
                Other
              </span>
            )}
          </div>
        ) : <span style={{ fontSize: 12, color: '#CBD5E1' }}>+ add</span>
      )}
    </div>
  );
};

// ─── Main view ────────────────────────────────────────────────────────────
// True on phone-sized screens - drives the phone rota below.
function useRotaIsPhone() {
  const q = '(max-width: 600px)';
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(q).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(q);
    const on = () => setPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return phone;
}

// Phone rota: pick a day, see everyone's hours for it. The full week grid is
// unusable on a phone (5 columns squashed), so this shows one day at a time with
// a day selector. Reuses RotaCell (tap to edit), coverage + week nav. Desktop
// keeps the grid.
function MobileRota({ entries, saveCell, holidays, weekStart, setWeekStart, weekDays, todayKey, gapsByDate }) {
  const inWeek = (k) => weekDays.some((d) => toDateKey(d) === k);
  const [selKey, setSelKey] = useState(() => (inWeek(todayKey) ? todayKey : toDateKey(weekDays[0])));
  useEffect(() => {
    if (!inWeek(selKey)) setSelKey(inWeek(todayKey) ? todayKey : toDateKey(weekDays[0]));
  }, [weekStart]);

  const DOW = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
  const dowIdx = (d) => (d.getDay() + 6) % 7;
  const selHoliday = holidays[selKey];
  const gaps = gapsByDate[selKey] || [];
  const covered = !selHoliday && gaps.length === 0;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <div style={{ flex: '0 0 auto', padding: '14px 14px 10px', borderBottom: '1px solid var(--line)', background: 'var(--bg-elev)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn" onClick={() => setWeekStart((w) => addDays(w, -7))} aria-label="Previous week"><Icon name="chevron" size={11} style={{ transform: 'rotate(90deg)' }}/></button>
          <div style={{ flex: 1, textAlign: 'center', fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{fmtWeekHeader(weekStart)}</div>
          <button className="btn" onClick={() => setWeekStart((w) => addDays(w, 7))} aria-label="Next week"><Icon name="chevron" size={11} style={{ transform: 'rotate(-90deg)' }}/></button>
          <button className="btn" onClick={() => setWeekStart(startOfWeek(new Date()))} style={{ marginLeft: 2 }}>Today</button>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          {weekDays.map((d) => {
            const k = toDateKey(d);
            const on = k === selKey;
            const isToday = k === todayKey;
            const hol = holidays[k];
            const dayCovered = !hol && (gapsByDate[k] || []).length === 0;
            return (
              <button key={k} onClick={() => setSelKey(k)}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '7px 0 6px', borderRadius: 9,
                  border: on ? '1.5px solid #0d9488' : '1px solid var(--line)', background: on ? 'rgba(13,148,136,0.10)' : (isToday ? '#FEF3C7' : 'var(--bg)'),
                  cursor: 'pointer', fontFamily: 'inherit' }}>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.03em', color: on ? '#0d9488' : 'var(--text-subtle)' }}>{DOW[dowIdx(d)]}</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{d.getDate()}</span>
                <span style={{ width: 6, height: 6, borderRadius: 99, background: hol ? '#94A3B8' : dayCovered ? '#22C55E' : '#EF4444' }}/>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ flex: '0 0 auto', padding: '10px 14px', fontSize: 12.5, lineHeight: 1.45,
        background: covered ? '#F0FDF4' : selHoliday ? '#F1F0EE' : '#FFFBEB',
        color: covered ? '#047857' : selHoliday ? '#57534E' : '#92400E',
        borderBottom: '1px solid var(--line)' }}>
        {selHoliday ? `Bank holiday - ${selHoliday}. No cover needed.`
          : covered ? 'Helpdesk covered 08:30 – 18:00 ✓'
          : `Still needs cover: ${gaps.map(fmtRange).join(', ')}`}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 24 }}>
        {USERS.map((u) => {
          const user = getUser(u.id);
          return (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: '1px solid var(--line)' }}>
              <Avatar user={user} size="sm"/>
              <span style={{ flex: '0 0 auto', width: 108, fontSize: 14, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || u.id}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <RotaCell value={entries[`${u.id}|${selKey}`]} onSave={(v) => saveCell(u.id, selKey, v)} isToday={selKey === todayKey} isWeekend={false} isHoliday={!!selHoliday} holidayName={selHoliday}/>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RotaView({ currentUser }) {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [entries, setEntries] = useState({});   // keyed by `${userId}|${date}` → hours
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [holidays, setHolidays] = useState({}); // { 'YYYY-MM-DD': 'Holiday name' } - England & Wales
  const isPhone = useRotaIsPhone();

  // Load UK bank holidays once (they don't change during a session). Backend-
  // sourced from gov.uk (see /api/bank-holidays). Fails soft - if it can't
  // load, the rota just doesn't grey any days.
  useEffect(() => {
    let cancelled = false;
    rotaApi('GET', '/bank-holidays')
      .then((h) => { if (!cancelled && h && typeof h === 'object') setHolidays(h); })
      .catch((e) => console.warn('[rota] bank holidays load failed:', e.message));
    return () => { cancelled = true; };
  }, []);

  // Mon-Fri only - the team doesn't work weekends and a 5-column grid is
  // tidier than 7 anyway. Server query is narrowed to the same range so we
  // don't pull weekend rows that we'd never show.
  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
  const fromDate = toDateKey(weekDays[0]);
  const toDate = toDateKey(weekDays[4]);

  const loadWeek = useCallback(async () => {
    try {
      const rows = await rotaApi('GET', `/rota?from=${fromDate}&to=${toDate}`);
      const map = {};
      for (const r of rows || []) {
        map[`${r.userId}|${r.date}`] = r.hours;
      }
      setEntries(map);
      setError(null);
    } catch (e) {
      console.warn('[rota] load failed:', e.message);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  // Initial + on week change.
  useEffect(() => { setLoading(true); loadWeek(); }, [loadWeek]);

  // Background refresh every 15s so edits from other team members appear
  // without a manual reload. Skipped when the tab is hidden to avoid waste.
  useEffect(() => {
    const tick = () => { if (!document.hidden) loadWeek(); };
    const id = setInterval(tick, 15000);
    const onVis = () => { if (!document.hidden) loadWeek(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [loadWeek]);

  const saveCell = async (userId, date, hours) => {
    const key = `${userId}|${date}`;
    // Optimistic update - show immediately, roll back if server rejects.
    const prev = entries[key];
    setEntries((m) => ({ ...m, [key]: hours }));
    try {
      if (hours) {
        await rotaApi('PUT', '/rota', { userId, date, hours });
      } else {
        // Empty string = delete the entry.
        await rotaApi('DELETE', `/rota?userId=${encodeURIComponent(userId)}&date=${date}`);
        setEntries((m) => {
          const copy = { ...m };
          delete copy[key];
          return copy;
        });
      }
    } catch (e) {
      console.warn('[rota] save failed:', e.message);
      // Roll back
      setEntries((m) => ({ ...m, [key]: prev || '' }));
      alert(`Save failed: ${e.message}`);
    }
  };

  const todayKey = toDateKey(new Date());
  // Grid: first col = person label, then one column per Mon-Fri day.
  // Names on the left reads more naturally as a "who's where this week"
  // board - your eye finds your own row first, then scans across.
  const cols = `180px repeat(${weekDays.length}, minmax(0, 1fr))`;

  // For each day, parse every team member's hours and check what slices of
  // 08:30-18:00 are NOT covered. Used to badge the day header and list the
  // gaps under the table.
  const gapsByDate = {};
  for (const day of weekDays) {
    const dateKey = toDateKey(day);
    // Bank holidays need no cover - treat as fully covered so they don't
    // trigger the "hours still need cover" warning.
    if (holidays[dateKey]) { gapsByDate[dateKey] = []; continue; }
    const shifts = USERS
      .map((u) => parseShift(entries[`${u.id}|${dateKey}`]))
      .filter(Boolean);
    gapsByDate[dateKey] = coverageGaps(shifts);
  }
  const coverageByDate = Object.fromEntries(
    Object.entries(gapsByDate).map(([k, gaps]) => [k, gaps.length === 0])
  );
  const allDaysCovered = weekDays.every((d) => coverageByDate[toDateKey(d)]);
  // Pre-build the list of "Day: gap1, gap2" rows shown in the warning.
  const uncoveredDays = weekDays
    .map((day) => ({
      day,
      gaps: gapsByDate[toDateKey(day)] || [],
    }))
    .filter(({ gaps }) => gaps.length > 0);

  // Phones get the purpose-built single-day view; desktop/tablet keep the grid.
  if (isPhone) {
    return (
      <MobileRota entries={entries} saveCell={saveCell} holidays={holidays}
        weekStart={weekStart} setWeekStart={setWeekStart} weekDays={weekDays}
        todayKey={todayKey} gapsByDate={gapsByDate}/>
    );
  }

  return (
    <div style={{ height: '100%', overflowY: 'auto', background: 'var(--bg)' }}>
      <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto', boxSizing: 'border-box' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 style={{
              margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)',
              fontFamily: 'Poppins, var(--font), sans-serif', letterSpacing: -0.3,
            }}>IT Rota</h1>
            <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text-muted)' }}>
              Click any cell to fill in. Everyone can edit. Updates every 15 seconds.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="btn"
              onClick={() => setWeekStart((w) => addDays(w, -7))}
              title="Previous week"
            >
              <Icon name="chevron" size={11} style={{ transform: 'rotate(90deg)' }}/>
            </button>
            <div style={{
              fontSize: 14, fontWeight: 600, color: 'var(--text)',
              padding: '6px 14px', minWidth: 160, textAlign: 'center',
            }}>
              {fmtWeekHeader(weekStart)}
            </div>
            <button
              className="btn"
              onClick={() => setWeekStart((w) => addDays(w, 7))}
              title="Next week"
            >
              <Icon name="chevron" size={11} style={{ transform: 'rotate(-90deg)' }}/>
            </button>
            <button
              className="btn"
              onClick={() => setWeekStart(startOfWeek(new Date()))}
              title="Jump to this week"
              style={{ marginLeft: 4 }}
            >
              Today
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA',
            borderRadius: 6, color: '#991B1B', fontSize: 13, marginBottom: 12,
          }}>
            {error}
          </div>
        )}

        {/* Grid: header row (dates) then one row per IT team member. */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: cols,
          background: 'var(--bg-elev)',
          border: '1px solid var(--line)',
          borderRadius: 8,
          overflow: 'hidden',
          opacity: loading && Object.keys(entries).length === 0 ? 0.5 : 1,
        }}>
          {/* Top-left corner */}
          <div style={{
            padding: '12px 14px',
            background: '#F1F5F9',
            borderBottom: '1px solid var(--line)',
            borderRight: '1px solid var(--line)',
            fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: 0.5,
          }}>Person</div>
          {/* Day column headers */}
          {weekDays.map((day) => {
            const dateKey = toDateKey(day);
            const isToday = dateKey === todayKey;
            const holiday = holidays[dateKey];
            const covered = coverageByDate[dateKey];
            return (
              <div key={dateKey} style={{
                padding: '10px 12px',
                background: holiday ? '#F1F0EE' : isToday ? '#FEF3C7' : '#F1F5F9',
                borderBottom: '1px solid var(--line)',
                borderRight: '1px solid var(--line)',
                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                minWidth: 0,
                position: 'relative',
              }}>
                {/* Coverage tick. Only shown when the team's combined hours
                    span 08:30-18:00 for this day. Suppressed on bank holidays
                    (no cover expected). Lives top-right so it doesn't compete
                    with the date text. */}
                {!holiday && covered && (
                  <span
                    title="Helpdesk covered 08:30 – 18:00"
                    style={{
                      position: 'absolute', top: 6, right: 8,
                      fontSize: 14, fontWeight: 700, color: '#047857',
                      lineHeight: 1,
                    }}
                  >✓</span>
                )}
                <div style={{
                  fontSize: 11, fontWeight: 700,
                  color: holiday ? '#A8A29E' : isToday ? '#92400E' : 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: 0.4,
                }}>
                  {day.toLocaleDateString('en-GB', { weekday: 'short' })}
                  {isToday && <span style={{ marginLeft: 6, color: '#B45309' }}>· today</span>}
                </div>
                <div style={{
                  fontSize: 15, fontWeight: 700,
                  color: holiday ? '#A8A29E' : isToday ? '#92400E' : 'var(--text)',
                  marginTop: 2,
                }}>
                  {day.getDate()} {day.toLocaleDateString('en-GB', { month: 'short' })}
                </div>
                {holiday && (
                  <div title={holiday} style={{
                    fontSize: 10, fontWeight: 600, color: '#A8A29E', marginTop: 3,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {holiday}
                  </div>
                )}
              </div>
            );
          })}

          {/* Person rows */}
          {USERS.map((u) => {
            const user = getUser(u.id);
            return (
              <React.Fragment key={u.id}>
                {/* Person label cell */}
                <div style={{
                  padding: '10px 14px',
                  background: 'var(--bg)',
                  borderRight: '1px solid var(--line)',
                  borderBottom: '1px solid var(--line)',
                  display: 'flex', alignItems: 'center', gap: 10,
                  minWidth: 0,
                }}>
                  <Avatar user={user} size="sm"/>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 700, color: 'var(--text)',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{user?.name || u.id}</div>
                  </div>
                </div>
                {/* One cell per day */}
                {weekDays.map((day) => {
                  const dateKey = toDateKey(day);
                  const isToday = dateKey === todayKey;
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                  return (
                    <RotaCell
                      key={`${u.id}|${dateKey}`}
                      value={entries[`${u.id}|${dateKey}`] || ''}
                      onSave={(v) => saveCell(u.id, dateKey, v)}
                      isToday={isToday}
                      isWeekend={isWeekend}
                      isHoliday={!!holidays[dateKey]}
                      holidayName={holidays[dateKey]}
                    />
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>

        {/* Coverage note. Only the WARNING state gets the amber box -
            success doesn't need to shout, so it's rendered as a plain
            footer line. Standing target: 08:30-18:00 Mon-Fri.
            Suppressed while `loading` is true so changing weeks doesn't
            flash the amber banner during the brief window where entries
            still holds last week's data and the new week's lookups all
            miss. `loading` is only set on mount / week-change, not on
            the 15-second background refresh, so the banner doesn't
            flicker every poll. */}
        {!loading && (allDaysCovered ? (
          <div style={{
            marginTop: 14, fontSize: 12.5, color: 'var(--text-muted)',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{ color: '#047857' }}>✓</span>
            <span>
              Helpdesk covered 08:30 – 18:00 Mon-Fri.
            </span>
          </div>
        ) : (
          <div style={{
            marginTop: 16, padding: '10px 14px',
            background: '#FFFBEB', border: '1px solid #FCD34D',
            borderRadius: 6, fontSize: 13, color: '#92400E',
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <span style={{ fontSize: 16, lineHeight: 1.4 }}>⚠</span>
            <div style={{ flex: 1 }}>
              <div>
                The IT helpdesk should be covered <strong>08:30 – 18:00, Monday to Friday</strong>.
                {' '}The following hours still need cover:
              </div>
              <ul style={{
                margin: '6px 0 0 0', padding: '0 0 0 18px',
                fontSize: 12.5, lineHeight: 1.5,
              }}>
                {uncoveredDays.map(({ day, gaps }) => (
                  <li key={toDateKey(day)}>
                    <strong>
                      {day.toLocaleDateString('en-GB', { weekday: 'long' })}
                    </strong>
                    {' '}({day.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}):{' '}
                    {gaps.map(fmtRange).join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}

        {/* Footer hint */}
        <div style={{
          marginTop: 10, fontSize: 12, color: 'var(--text-muted)',
          display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
        }}>
          <span>Click any cell to edit. Use quick chips for common patterns.</span>
          <span>·</span>
          <span>Last loaded: {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
      </div>
    </div>
  );
}

export { RotaView };
